/**
 * REQUEST Availability Type Configuration
 *
 * Default zone configurations for REQUEST availability type.
 * Used when timeslots require approval (booking requests).
 * Journeys can override specific properties for journey-specific UX.
 */

import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { AvailabilityTypePageConfig } from '../models/checkout-journey.types';
import { CheckoutStore } from '../checkout.store';
import { JourneyStore } from '../journey.store';
import { CheckoutService } from '../checkout.service';
import { AssociationsStore } from '@app/features/associations/store/associations.store';
import { BasketStore } from '@app/features/associations/components/basket/basket.store';
import { navigateToLastUserSearch } from './checkout-journeys.config';

export const REQUEST_AVAILABILITY_TYPE_CONFIG: AvailabilityTypePageConfig[] = [
  {
    pageRoute: 'addons',
    zoneConfig: {
      actions: [
        {
          id: 'next',
          buttonLabel: 'Videre til Foreningsinfo',
          action: 'next',
          disabled: () => false, // Can be overridden by journey
        },
      ],
    },
  },
  {
    pageRoute: 'association-info',
    zoneConfig: {
      actions: [
        {
          id: 'next',
          buttonLabel: 'Næste',
          action: 'next',
        },
      ],
    },
  },
  {
    pageRoute: 'summary',
    zoneConfig: {
      actions: [
        {
          id: 'submit',
          buttonLabel: 'Send forespørgsel',
          action: 'submit',
          callback: async () => {
            const checkoutStore = inject(CheckoutStore);
            const basketStore = inject(BasketStore);
            const checkoutService = inject(CheckoutService);
            const router = inject(Router);

            try {
              await firstValueFrom(checkoutService.submitBookingApproval(checkoutStore.bookingId()));
              checkoutStore.clearCheckoutContext();
              basketStore.clearBasket();
              await router.navigate(['/checkout/complete'], {
                queryParams: { uid: checkoutStore.booking()?.uid },
              });
            } catch {
              await router.navigate(['/checkout/error']);
            }
          },
        },
      ],
    },
  },
  {
    pageRoute: 'complete',
    zoneConfig: {
      summaryContent: {
        title: 'Din forespørgsel er sendt',
        message: '- Vi sender en mail til dig, når afgørelsen er taget.',
        illustrationPath: './assets/illustrations/checkout-no-goalline.svg',
      },
      actions: [
        {
          id: 'primary-action',
          buttonLabel: 'Søg efter nye tider',
          buttonType: 'filled',
          action: 'custom',
          callback: async () => {
            const journeyStore = inject(JourneyStore);
            const router = inject(Router);
            await navigateToLastUserSearch(journeyStore, router);
          },
        },
        {
          id: 'secondary-action',
          buttonLabel: 'Se dine forespørgsler',
          buttonType: 'outlined',
          action: 'custom',
          callback: async () => {
            const associationsStore = inject(AssociationsStore);
            const router = inject(Router);
            const entityId = associationsStore.selectedEntityId();
            await router.navigate([`/associations/${entityId}/bookings`], {
              queryParams: { tab: 'pending' },
            });
          },
        },
      ],
    },
  },
];
